import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalizedLink as Link } from './LocalizedLink';
import { X, PlayCircle } from 'lucide-react';
import { trackBannerView, trackBannerClick, trackBannerDismiss, trackBannerCopy } from '../services/analytics';
import './DemoBanner.css';
import { useTranslation } from "react-i18next";
import { useAuth } from '../context/AuthContext';

export default function DemoBanner() {
    const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasTrackedView = useRef(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const isDashboard = location.pathname.startsWith('/dashboard');
  const isReviewGate = location.pathname.startsWith('/r/');
  const shouldShow = isVisible && !isDashboard && !isReviewGate;

  useEffect(() => {
    const dismissed = sessionStorage.getItem('demoBannerDismissed');
    if (dismissed === 'true') {
      setIsVisible(false);
    }
  }, []);

  // Dynamically measure banner height and set CSS variable
  useEffect(() => {
    if (!shouldShow) {
      document.documentElement.style.setProperty('--demo-banner-height', '0px');
      return;
    }

    // Track impression once per session
    if (!hasTrackedView.current) {
      trackBannerView();
      hasTrackedView.current = true;
    }

    // Measure actual banner height and update CSS variable
    const el = bannerRef.current;
    if (el) {
      const updateHeight = () => {
        const h = el.offsetHeight;
        document.documentElement.style.setProperty('--demo-banner-height', `${h}px`);
      };
      updateHeight();

      const ro = new ResizeObserver(updateHeight);
      ro.observe(el);
      return () => {
        ro.disconnect();
        document.documentElement.style.setProperty('--demo-banner-height', '0px');
      };
    }

    return () => {
      document.documentElement.style.setProperty('--demo-banner-height', '0px');
    };
  }, [shouldShow]);

  const handleDismiss = () => {
    trackBannerDismiss();
    setIsVisible(false);
    sessionStorage.setItem('demoBannerDismissed', 'true');
  };

  const handleCtaClick = async () => {
    trackBannerClick();
    setLoading(true);
    try {
      await login('demo@reviio.app', 'Test1234!');
      navigate('/dashboard');
    } catch (err) {
      console.error('Demo login failed', err);
    } finally {
      setLoading(false);
    }
  };



  if (!shouldShow) return null;

  return (
    <div className="demo-banner-wrapper" ref={bannerRef}>
      <div className="demo-banner">
        <div className="demo-banner-content">
          <span className="demo-banner-badge">{t('demoBanner.liveDemo')}</span>
          <p className="demo-banner-text">{' ' + t('demoBanner.seeWhatReviioCanDoFor')}
          </p>
          <button className="demo-banner-btn" onClick={handleCtaClick} disabled={loading}>
            {loading ? <span className="spinner spinner-sm" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <><PlayCircle size={14} />{' ' + t('demoBanner.openDashboard')}</>}
          </button>
        </div>
        <button className="demo-banner-close" onClick={handleDismiss} aria-label={t('demoBanner.closeDemoBanner')}>
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

